import { forwardRef } from 'react';
import { Link } from 'react-router-dom';

import { useOptionalLocale } from '@/core/I18nProvider';
import { localizedPath, stripLocalePrefix } from '@/i18n/locale-path';

import type { LinkProps } from 'react-router-dom';

/**
 * Locale-aware drop-in for react-router's `<Link>` (GA-13 / ADR-0133).
 *
 * The marketing site publishes every route in all six locales (English
 * unprefixed; others under `/<locale>/…`). A visitor browsing in Spanish must
 * stay in Spanish when they click an internal link, so every internal `<Link>`
 * routes to the active locale's URL.
 *
 * This wrapper rewrites a site-relative string `to` (a path beginning with a
 * single `/`) to `localizedPath(stripLocalePrefix(to), locale)`:
 *   - `stripLocalePrefix` first, so a `to` that already carries a locale prefix
 *     (or an English-relative path) both normalise to the same English-relative
 *     base — the rewrite is idempotent and prefix-agnostic.
 *   - `localizedPath` then re-applies the *active* locale (English → no prefix).
 *
 * Anything that is not a site-relative string path passes through untouched:
 * external URLs (`https://…`, `mailto:`), protocol-relative (`//…`), in-page
 * hashes (`#section`), and `Partial<Path>` / relative `to` objects. Consumers
 * import this as `Link` (a one-line import swap) so existing JSX is unchanged.
 */
export const LocalizedLink = forwardRef<HTMLAnchorElement, LinkProps>(function LocalizedLink(
  { to, ...rest },
  ref,
) {
  const locale = useOptionalLocale();
  const isInternalPath = typeof to === 'string' && to.startsWith('/') && !to.startsWith('//');
  const resolved = isInternalPath ? localizedPath(stripLocalePrefix(to), locale) : to;
  return <Link ref={ref} to={resolved} {...rest} />;
});
